import { ApolloClient, ApolloLink, HttpLink, Operation } from '@apollo/client';
import { NetworkError } from '@apollo/client/errors';
import { onError } from '@apollo/client/link/error';
import { Body } from '@apollo/client/link/http/selectHttpOptionsAndBody';
import { RetryLink } from '@apollo/client/link/retry';
import { GraphQLFormattedError } from 'graphql';
import loggingService from '../services/logging';
import sleep from '../utils/sleep';
import { createAuthLink } from './authLink';
import { appVersionVar, cache } from './cache';
import createPersistedQueryLink, {
  createPersistedQueryManifestVerificationLink,
} from './persistedQueryLink';

export type ApolloClientInitOptions = {
  onInvalidAuth?: () => void;
  endpoint?: string;
  onNetworkError?: (error: NonNullable<NetworkError>, operation: Operation) => void;
  onGraphQLError?: (graphQLErrors: readonly GraphQLFormattedError[], operation: Operation) => void;
};

export default function initApolloClient({
  onInvalidAuth,
  endpoint,
  onNetworkError,
  onGraphQLError,
}: ApolloClientInitOptions = {}) {
  const logger = loggingService.getLogger();

  // Transparently handles re-authentication and setting auth headers
  const authLink = createAuthLink({
    onInvalidAuth,
    logger,
  });

  // Handle errors by delegating them to passed-in handlers
  const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    // Pass on network errors
    if (networkError && onNetworkError) {
      onNetworkError(networkError, operation);
    }

    // Pass on GraphQL errors
    if (graphQLErrors && onGraphQLError) {
      onGraphQLError(graphQLErrors, operation);
    }
  });

  // Retry on network or server errors (this does not cover application errors)
  const retryLink = new RetryLink({
    delay: {
      initial: 1_000,
      max: 10_000,
      jitter: true, // randomize delays between attempts to avoid thundering herd problem
    },
    attempts: {
      max: 3,
      retryIf: (_error, operation) =>
        new Promise((resolve) => {
          const context = { operationName: operation.operationName };

          // Do not retry mutations. We should always see an OperationDefinition.
          const operationDef = operation.query.definitions.find(
            (def) => def.kind === 'OperationDefinition',
          );
          if (!operationDef || operationDef.operation !== 'query') {
            logger.debug('Skipping retry for non-query', { context });

            resolve(false);
            return;
          }

          // Hold before next attempt in case we have no network connection at all
          if (!navigator.onLine) {
            let timeout: number | null = null;

            const onlineHandler = () => {
              if (onlineHandler !== null) {
                window.removeEventListener('online', onlineHandler);
              }

              if (timeout !== null) {
                window.clearTimeout(timeout);
              }

              logger.debug('Connection established, retrying request', { context });

              resolve(true);
            };

            // Note that 'online' does not guarantee internet connectivity, just any network connection
            window.addEventListener('online', onlineHandler);

            // Abort after 10 minutes which is the time we will cache responses on the backend
            const ABORT_RETRIES_AFTER_TIME = 600_000;
            timeout = window.setTimeout(() => {
              if (onlineHandler !== null) {
                window.removeEventListener('online', onlineHandler);
              }

              logger.debug('Aborting retry after 10 minutes', { context });

              resolve(false);
            }, ABORT_RETRIES_AFTER_TIME);

            logger.debug('Connection offline, waiting for connectivity to retry request', {
              context,
            });

            return;
          }

          // Retry in all other cases
          logger.debug('Request failed, retrying', { context });
          resolve(true);
        }),
    },
  });

  // Send via HTTP (terminating link)
  const httpLink = new HttpLink({
    uri: endpoint,
    credentials: 'same-origin',
    fetch: async (input: RequestInfo | URL, init?: RequestInit) => {
      let opName = '';

      if (init?.body instanceof FormData) {
        const operations = init.body.get('operations');
        if (typeof operations === 'string') {
          opName = (JSON.parse(operations) as Body).operationName ?? '';
        }
      } else if (init?.body) {
        // eslint-disable-next-line @typescript-eslint/no-base-to-string
        opName = (JSON.parse(String(init.body)) as Body).operationName ?? '';
      }

      const response = await fetch(
        `${endpoint}${endpoint?.includes('?') ? '&' : '?'}opname=${opName}`,
        init,
      );

      // Check for Cloudflare challenges
      if (response.headers.get('cf-mitigated') === 'challenge') {
        // If we get a challenge, we reload the page to show it.
        window.location.reload();

        // Delay execution so processing does not continue before the reload happens.
        await sleep(1000);

        return response;
      }

      // Store the app version on the backend in a reactive variable
      const appVersion = response.headers.get('App-Version');
      appVersionVar(appVersion);

      return response;
    },
  });

  return new ApolloClient({
    devtools: {
      enabled: import.meta.env.DEV,
    },
    // persistedQueryLink must stay directly in front of the terminating link: it rewrites the
    // request body, so anything after it would not see the change.
    link: ApolloLink.from([
      errorLink,
      retryLink,
      authLink,
      createPersistedQueryManifestVerificationLink(logger),
      createPersistedQueryLink(),
      httpLink,
    ]),
    cache,
    defaultOptions: {
      // watchQuery is internally used by useQuery and useLazyQuery
      watchQuery: {
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'none',
      },
    },
  });
}
